import {
  isAggregateColumnNameTaken,
  isAddAggregateSaveDisabled,
} from "../utils/addAggregateUtils";
import { useIsExpressionValid } from "../utils/expressionEditorUtils";
import { ExpressionEditor } from "./ExpressionEditor";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
} from "@mui/material";
import Button from "components/common/Button";
import * as labels from "config.labels";
import React from "react";
import * as staticDataHooks from "store/hooks/staticDataHooks";

export const RenameColumnDialog = React.memo(
  ({
    open,
    value,
    onClose,
    onConfirm,
    onChange,
    onRemove,
    removeLabel,
    hasOverride = false,
  }) => {
    const translateLabels = staticDataHooks.useTranslator("uiLabels");
    const resolvedRemoveLabel =
      removeLabel ??
      translateLabels(labels.RENAME_DIALOG_REMOVE, { default: "Remove" });
    return (
      <Dialog open={open} onClose={onClose} maxWidth={"xs"} fullWidth>
        <DialogTitle>
          {translateLabels(labels.RENAME_DIALOG_TITLE, {
            default: "Rename Column",
          })}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin={"dense"}
            label={translateLabels(labels.RENAME_DIALOG_COLUMN_NAME, {
              default: "Column Name",
            })}
            fullWidth
            variant={"outlined"}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onConfirm();
              } else if (e.key === "Escape") {
                onClose();
              }
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 3,
            pb: 2,
          }}
        >
          {hasOverride && onRemove && (
            <Button onClick={onRemove} color={"secondary"} sx={{ mr: "auto" }}>
              {resolvedRemoveLabel}
            </Button>
          )}
          <Button onClick={onClose} color={"secondary"}>
            {translateLabels(labels.RENAME_DIALOG_CANCEL, {
              default: "Cancel",
            })}
          </Button>
          <Button onClick={onConfirm} color={"primary"}>
            {translateLabels(labels.RENAME_DIALOG_CONFIRM, {
              default: "Confirm",
            })}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);
RenameColumnDialog.displayName = "RenameColumnDialog";

export const ConfirmDialog = React.memo(
  ({
    open,
    title,
    message,
    confirmLabel,
    cancelLabel,
    confirmColor = "primary",
    onConfirm,
    onCancel,
  }) => {
    const translateLabels = staticDataHooks.useTranslator("uiLabels");
    const resolvedConfirmLabel =
      confirmLabel ??
      translateLabels(labels.CONFIRM_DIALOG_CONFIRM, { default: "Confirm" });
    const resolvedCancelLabel =
      cancelLabel ??
      translateLabels(labels.CONFIRM_DIALOG_CANCEL, { default: "Cancel" });
    return (
      <Dialog open={open} onClose={onCancel} maxWidth={"xs"} fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>{message}</DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 3,
            pb: 2,
          }}
        >
          <Button onClick={onCancel} color={"secondary"}>
            {resolvedCancelLabel}
          </Button>
          <Button onClick={onConfirm} color={confirmColor}>
            {resolvedConfirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);
ConfirmDialog.displayName = "ConfirmDialog";

export const AddAggregateDialog = React.memo(
  ({
    open,
    onClose,
    onSave,
    onDelete,
    availableColumns = [],
    existingAggregateColumns = [],
    initialColumnName,
    initialExpression,
  }) => {
    const effectiveInitialColumnName = (initialColumnName || "").trim();
    const [columnName, setColumnName] = React.useState("");
    const [expression, setExpression] = React.useState({});

    React.useEffect(() => {
      if (open) {
        setColumnName(effectiveInitialColumnName);
        setExpression(initialExpression ?? {});
      }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const isNameTaken = React.useMemo(
      () =>
        isAggregateColumnNameTaken(
          columnName,
          availableColumns,
          existingAggregateColumns,
          effectiveInitialColumnName
        ),
      [
        columnName,
        availableColumns,
        existingAggregateColumns,
        effectiveInitialColumnName,
      ]
    );

    const translateLabels = staticDataHooks.useTranslator("uiLabels");
    const isExpressionValid = useIsExpressionValid();
    const isSaveDisabled = isAddAggregateSaveDisabled(
      columnName,
      isNameTaken,
      expression,
      isExpressionValid
    );

    const handleSave = () => {
      onSave({ columnName, expression });
      setColumnName("");
      setExpression({});
    };

    const handleClose = () => {
      setColumnName("");
      setExpression({});
      onClose();
    };

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        slotProps={{
          paper: {
            sx: {
              width: "90vw",
              height: "min(80vh, calc(100vh - 100px))",
              display: "flex",
              flexDirection: "column",
            },
          },
        }}
      >
        <DialogTitle>
          {translateLabels(labels.ADD_AGGREGATE_DIALOG_TITLE, {
            default: "Add Function Column",
          })}
        </DialogTitle>
        <DialogContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: 2,
            overflow: "auto",
          }}
        >
          <TextField
            autoFocus
            label={translateLabels(labels.ADD_AGGREGATE_DIALOG_COLUMN_NAME, {
              default: "Column Name",
            })}
            fullWidth
            variant={"outlined"}
            value={columnName}
            onChange={(e) => setColumnName(e.target.value)}
            error={isNameTaken}
            helperText={
              isNameTaken
                ? translateLabels(labels.ADD_AGGREGATE_DIALOG_NAME_TAKEN, {
                    default: "A column with this name already exists",
                  })
                : ""
            }
            sx={{ mt: 1 }}
          />
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <ExpressionEditor
              availableColumns={availableColumns}
              expression={expression}
              onChange={setExpression}
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 3,
            pb: 2,
          }}
        >
          {onDelete && (
            <Button onClick={onDelete} color={"secondary"} sx={{ mr: "auto" }}>
              {translateLabels(labels.ADD_AGGREGATE_DIALOG_DELETE, {
                default: "Delete",
              })}
            </Button>
          )}
          <Button onClick={handleClose} color={"secondary"}>
            {translateLabels(labels.ADD_AGGREGATE_DIALOG_CANCEL, {
              default: "Cancel",
            })}
          </Button>
          <Button
            onClick={handleSave}
            color={"primary"}
            isDisabled={isSaveDisabled}
          >
            {translateLabels(labels.ADD_AGGREGATE_DIALOG_SAVE, {
              default: "Save",
            })}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);
AddAggregateDialog.displayName = "AddAggregateDialog";
